import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { AppBar, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import AccountIcon from '../../assets/icons/AccountIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import MenuIcon from '../../assets/icons/MenuIcon';
import { ConditionalLink } from '../../components/ConditionalLink';
import IKImage from '../../components/IKImage/IKImage';
import NavigationOverlay from '../../components/NavigationOverlay/NavigationOverlay';
import { SearchInput } from '../../components/SearchInput';
import { CustomButton } from '../../components/UI/CustomButton';
import { SecondaryButton } from '../../components/UI/SecondaryButton';
import { UserInfoCtx } from '../../containers/CommonLayout/CommonLayout';
import { CourseDropdownItem } from '../../containers/Layout/Layout';
import useScriptLoader, { SCRIPTS } from '../../context/ScriptLoader/useScriptLoader';
import { GET_LIST_BASE_WITH_SORT } from '../../graphql/analytics';
import { GET_HAS_NEWS } from '../../graphql/local';
import { GET_TWO_LATEST_VIDEOS } from '../../graphql/videos';
import { USER_ROLES } from '../../pages/const';
import styled from '../../styled';
import { colorsPalette, MIDDLE_MARGIN_PX, SUBMIDDLE_MARGIN_PX, useIsWidthUp } from '../../theme';
import {
  Navigation,
  NavigationItem,
  NavigationItemType,
  Query,
  QueryListBaseVideoCategoriesArgs,
  SortOrder,
} from '../../types';
import { checkIsIframed, getCourseUrl, replaceByIndex, translate } from '../../utils/helpers';
import { useAuth, useEventListener } from '../../utils/hooks';
import LogoutMegaMenuFooter from './components/LogoutMegaMenuFooter';
import { MenuItems } from './components/MenuItems';
import SafeLink from './components/SafeLink/SafeLink';
import { UserInfoDropdown } from './components/UserInfoDropdown';
import { formatMegaMenuItem, LOGGED_IN_ITEMS, PROFILE_LINKS } from './const';
import useStyles from './styles';

export const LOGO_TRANSFORMATION = [
  {
    height: 120,
  },
];

const CATEGORIES_FILTER = {
  sort: [
    {
      field: 'title',
      order: SortOrder.Asc,
    },
  ],
};

const getButtonProps = (url, location, loginWithPopup) => {
  if (url === '/login') {
    if (checkIsIframed()) {
      return {
        to: '/login',
        onClick: (e) => {
          e.preventDefault();
          loginWithPopup();
        },
      };
    } else {
      return {
        to: {
          pathname: '/login',
          state: { prevPath: location.pathname, prevSearch: location.search },
        },
      };
    }
  } else {
    return {
      to: url,
    };
  }
};

interface HeaderProps {
  logoUrl?: string | null;
  onlyLogo?: boolean;
  navigation?: Navigation;
  assignedLearningsName?: string;
  dropdownCourses?: CourseDropdownItem[];
  isHeaderHidden?: boolean;
}

export const Header: React.FC<HeaderProps> = ({
  logoUrl,
  navigation,
  onlyLogo = false,
  assignedLearningsName,
  dropdownCourses,
  isHeaderHidden = false,
}) => {
  const { t } = useTranslation();
  const [pageYOffset, setPageYOffset] = useState(0);
  const [isSearchOpen, setSearchOpenState] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const [userInfoOpened, setUserInfoOpened] = useState(false);
  const location = useLocation();
  const { userInfo } = useContext(UserInfoCtx);

  const { isAuthenticated, loginWithRedirect, loginWithPopup, logout } = useAuth();
  const isUserComLoaded = useScriptLoader(SCRIPTS.USER_COM);

  const classes = useStyles(pageYOffset, isHeaderHidden)();
  const isTablet = !useIsWidthUp('xl');
  const isMobile = !useIsWidthUp('md');
  const userName = userInfo?.name;

  const toggleMenu = () => setMenuOpened((prevState) => !prevState);
  const toggleUserInfo = () => setUserInfoOpened((prevState) => !prevState);

  const { data } = useQuery(GET_HAS_NEWS);
  const hasNews = data?.hasNews;

  const { data: baseCategoriesData } = useQuery<
    Pick<Query, 'listBaseVideoCategories'>,
    QueryListBaseVideoCategoriesArgs
  >(GET_LIST_BASE_WITH_SORT, {
    skip: !userInfo,
    variables: CATEGORIES_FILTER,
  });

  const { data: twoLatestVideosData } = useQuery<
    Pick<Query, 'getLastAddedTwoVideosFromDifferentExperts'>
  >(GET_TWO_LATEST_VIDEOS, {
    skip: !userInfo,
  });

  useEffect(() => {
    if (menuOpened) {
      setUserInfoOpened(false);
    }
  }, [menuOpened]);

  useEffect(() => {
    if (userInfoOpened) {
      setMenuOpened(false);
    }
  }, [userInfoOpened]);

  const navigationItems: NavigationItem[] = useMemo(() => {
    return navigation?.items
      ? (navigation?.items.filter(
          (item) =>
            item?.item_type === NavigationItemType.Item ||
            item?.item_type === NavigationItemType.MegaMenuItem,
        ) as NavigationItem[])
      : [];
  }, [navigation]);

  const teamName = useMemo(() => {
    if (userInfo?.teams && userInfo.teams[0]) {
      return userInfo?.teams[0]?.name;
    }

    return null;
  }, [userInfo]);

  const toggleSearch = () => {
    setSearchOpenState(!isSearchOpen);
  };

  const handleOpenUserComWidget = () => {
    //@ts-ignore
    if (window?.userengage) {
      //@ts-ignore
      window.userengage('widget.open');
    }
  };

  const handleClickContact = () => {
    handleOpenUserComWidget();
    toggleUserInfo();
  };

  useEventListener(
    'scroll',
    () => {
      if (window) {
        const newPageYOffset = window.pageYOffset;

        if (pageYOffset === 0 && newPageYOffset > 0) {
          setPageYOffset(newPageYOffset);
        } else if (pageYOffset > 0 && newPageYOffset === 0) {
          setPageYOffset(newPageYOffset);
        }
      }
    },
    undefined,
    { passive: true },
  );

  const buttonData = navigationItems?.find((item) => item.type === 'Button');
  const filledButtonData = navigationItems?.find((item) => item.type === 'FilledButton');
  const linksData = navigationItems?.filter(
    (item) => item.type === 'Link' || item.type === 'MegaMenu',
  );
  const userData = navigationItems?.find((item) => item.type === 'User');
  const loginButton = navigationItems?.find((item) => item.type === 'LoginButton');

  const chosenItems = useMemo(() => {
    if (userInfo) {
      const arr = LOGGED_IN_ITEMS.map((item) => {
        if (item.type === 'MegaMenu') {
          return formatMegaMenuItem(
            item,
            t,
            hasNews,
            baseCategoriesData?.listBaseVideoCategories,
            twoLatestVideosData?.getLastAddedTwoVideosFromDifferentExperts,
          );
        }

        return {
          ...item,
          title: translate(`header.${item.title}`),
        };
      });

      if (
        (userInfo?.teams && userInfo.teams.length > 0) ||
        userInfo?.role === USER_ROLES.CompanyOwner ||
        userInfo?.role === USER_ROLES.CompanyManager
      ) {
        const assignedLearningTitle = assignedLearningsName
          ? assignedLearningsName
          : t('header.assigned');
        const url = userInfo?.is_manager ? '/assigned-learning/dashboard' : '/assigned-learning';

        arr.push({
          url,
          title: `${assignedLearningTitle} ${t('header.learning')}`,
          subItems: [],
          type: '',
        });
      }

      return arr;
    } else {
      let loggedOutLinksData: NavigationItem[] = [];

      const indexOfCourseLink = linksData.findIndex((item) => item?.url === '/courses');

      if (
        indexOfCourseLink !== -1 &&
        dropdownCourses &&
        dropdownCourses?.length === 1 &&
        dropdownCourses[0]
      ) {
        loggedOutLinksData = replaceByIndex(linksData, indexOfCourseLink, {
          ...loggedOutLinksData[indexOfCourseLink],
          url: getCourseUrl(dropdownCourses[0]?.slug) || '',
        });
      } else {
        loggedOutLinksData = linksData;
      }

      return loggedOutLinksData;
    }
  }, [
    linksData,
    userInfo,
    assignedLearningsName,
    t,
    dropdownCourses,
    hasNews,
    baseCategoriesData,
    twoLatestVideosData,
  ]);

  const handleClickMenuLink = useCallback(() => {
    setMenuOpened(false);
  }, []);

  const handleLogout = useCallback(async () => {
    if (isUserComLoaded && window?.UE?.resetAuth) {
      await window?.UE?.resetAuth({
        apiKey: process.env.REACT_APP_USERCOM_API_KEY,
      });
    }

    logout(
      checkIsIframed()
        ? {
            returnTo: `${window?.location?.origin}/?logout`,
          }
        : undefined,
    );
  }, [logout, isUserComLoaded]);

  const handleLogin = () => {
    const loginOptions = {
      redirectUri:
        location.pathname !== '/home' ? `${location.pathname}${location.search}` : '/home',
      isDirectUrl: location.pathname === '/home' || location.pathname === '/',
    };

    if (checkIsIframed()) {
      loginWithPopup();
      setMenuOpened(false);
    } else {
      loginWithRedirect(loginOptions);
    }
  };

  //used to close mobile menu when click on link
  useEffect(() => {
    setMenuOpened(false);
  }, [location]);

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={0}
      className={cx(classes.header, isSearchOpen && classes.openSearch)}
    >
      <Toolbar className={cx(classes.toolBar, pageYOffset > 0 && 'scrolled-toolbar')}>
        <div className={classes.leftPart}>
          <div
            className={cx(
              classes.logoWrapper,
              isSearchOpen && classes.openSearch,
              pageYOffset > 0 ? 'header_logo_small' : 'header_logo',
            )}
          >
            <ConditionalLink withLink={!onlyLogo} to={userInfo ? '/home' : '/'}>
              {logoUrl && (
                <IKImage
                  path={logoUrl || ''}
                  draggable={false}
                  alt={'logo'}
                  transformation={LOGO_TRANSFORMATION}
                  // We want to disable srcset for logo for now. It causes an issue when we have custom CSS styles for diff logo and they change the width
                  // srcset={[{ width: 141 }, { width: 175 }]}
                  // sizes={`(min-width:${BREAKPOINTS_VALUES["sm"]}px) 175px, 141px`}
                />
              )}
            </ConditionalLink>
          </div>
          {!isSearchOpen && !onlyLogo && (
            <Box className={classes.topNavMenu}>
              <MenuItems
                items={chosenItems}
                hasSubscription={Boolean(userInfo?.has_subscription)}
                isMobile={isTablet}
                scrolled={pageYOffset > 0}
                isManager={userInfo?.is_manager}
              />
            </Box>
          )}
        </div>
        {onlyLogo ? (
          <div></div>
        ) : (
          <>
            <Box className={cx(classes.topBar, isSearchOpen && classes.openSearch)}>
              {menuOpened ? (
                <StyledCloseIcon onClick={toggleMenu} />
              ) : (
                <StyledMenuIcon onClick={toggleMenu} />
              )}
              {!userInfo && (
                <>
                  <div>
                    {buttonData && !isMobile && (
                      <SafeLink
                        className={classes.joinBtnLink}
                        {...getButtonProps(buttonData?.url, location, loginWithPopup)}
                      >
                        <CustomButton btnClass={classes.joinBtn}>{buttonData?.title}</CustomButton>
                      </SafeLink>
                    )}
                    {loginButton && !isMobile && (
                      <CustomButton btnClass={classes.joinBtn} onClick={handleLogin}>
                        {loginButton?.title}
                      </CustomButton>
                    )}
                    {filledButtonData && !isMobile && (
                      <SafeLink className={classes.joinBtnLink} to={filledButtonData?.url || '#'}>
                        <SecondaryButton btnClass={classes.joinBtn}>
                          {filledButtonData?.title}
                        </SecondaryButton>
                      </SafeLink>
                    )}
                  </div>
                  {userData && (
                    <a
                      href={userData?.url || '#'}
                      className={cx(classes.accBtn, isSearchOpen && classes.openSearch)}
                    >
                      <AccountIcon
                        color={colorsPalette.white}
                        aria-label="account of current user"
                      />
                    </a>
                  )}
                </>
              )}
              {userInfo && (
                <>
                  <Typography className={classes.username}>{userName}</Typography>
                  <UserInfoDropdown
                    teamName={teamName}
                    scrolled={pageYOffset > 0}
                    handleOpenUserComWidget={handleOpenUserComWidget}
                    handleLogout={handleLogout}
                    onClick={toggleUserInfo}
                  />
                  <SearchInput
                    wrapperClass={cx(classes.searchWrapper, isSearchOpen && classes.openSearch)}
                    isOpen={isSearchOpen}
                    toggle={toggleSearch}
                  />
                </>
              )}
            </Box>
            <NavigationOverlay opened={menuOpened} startBreakpoint="xl">
              <>
                <Box className={cx(classes.overlayContent, userInfo && 'without-last-border')}>
                  <Box className={classes.overlayContainer}>
                    <MenuItems
                      wrapperClass={!userInfo && buttonData ? classes?.menuItemsWithMargin : ''}
                      items={chosenItems}
                      isManager={userInfo?.is_manager}
                      hasSubscription={Boolean(userInfo?.has_subscription)}
                      isMobile={isTablet}
                      handleClickLink={handleClickMenuLink}
                    />
                  </Box>

                  {isAuthenticated ? (
                    <button onClick={handleLogout} className={classes.overlayBottomLink}>
                      {t('header.logout')}
                    </button>
                  ) : (
                    <StyledLogoutMegaMenuFooter
                      mainBtnProps={getButtonProps(buttonData?.url, location, loginWithPopup)}
                      mainBtnText={buttonData?.title}
                      additionalBtnLink={filledButtonData?.url}
                      additionalBtnText={filledButtonData?.title}
                    />
                  )}
                </Box>
              </>
            </NavigationOverlay>
            {userInfo && (
              <NavigationOverlay opened={userInfoOpened} startBreakpoint="xl">
                <Box className={cx(classes.overlayContent, userInfo && 'without-last-border')}>
                  {userInfo && (
                    <>
                      <div className={classes.overlayMainContent}>
                        <Box className={classes.userInfoWrapper}>
                          <Typography className={classes.username}>{userName}</Typography>
                          {teamName && <Typography className={classes.team}>{teamName}</Typography>}
                        </Box>
                        <Box className={cx(classes.accordionLinks)}>
                          {PROFILE_LINKS.map((item) => (
                            <Box key={item.url} className={classes.linkWrapper}>
                              <Link
                                to={item.url}
                                onClick={() => setUserInfoOpened(false)}
                                className={classes.link}
                              >
                                {t(item.title)}
                              </Link>
                            </Box>
                          ))}
                          {
                            // @ts-ignore
                            !!window?.userengage && (
                              <Box className={classes.linkWrapper}>
                                <div className={classes.link} onClick={handleClickContact}>
                                  {t('header.contact')}
                                </div>
                              </Box>
                            )
                          }
                        </Box>
                      </div>
                    </>
                  )}
                  <button onClick={handleLogout} className={classes.overlayBottomLink}>
                    {t('header.logout')}
                  </button>
                </Box>
              </NavigationOverlay>
            )}
            <HeaderBackground
              show={menuOpened || userInfoOpened}
              scrolled={pageYOffset > 0}
            ></HeaderBackground>
          </>
        )}
      </Toolbar>
      {isSearchOpen && <FullscreenOverlay onClick={toggleSearch} />}
    </AppBar>
  );
};

export default Header;

const FullscreenOverlay = styled('div')`
  position: fixed;
  width: 100%;
  height: 100%;
  background: rgba(27, 25, 37, 0.82);
`;

const createMenuIcon = (tag) => styled(tag)`
  width: 24px;
  min-width: 24px;
  height: 24px;
  margin-left: ${SUBMIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 36px;
    min-width: 36px;
    height: 36px;
    margin-left: ${MIDDLE_MARGIN_PX};
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;

const StyledMenuIcon = createMenuIcon(MenuIcon);
const StyledCloseIcon = createMenuIcon(CloseIcon);

const StyledLogoutMegaMenuFooter = styled(LogoutMegaMenuFooter, {
  shouldForwardProp: (prop) => prop !== 'wrapperWidth',
})`
  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;

const HeaderBackground = styled('div', {
  shouldForwardProp: (prop) => prop !== 'scrolled' && prop !== 'show',
})`
  width: ${({ show }) => (show ? '100%' : '0')};
  height: ${({ scrolled }) => (scrolled ? '74px' : '114px')};
  position: absolute;
  top: 0;
  right: 0;
  background: ${colorsPalette.blueDark};
  z-index: 1200;
  transition: 0.5s ease-in-out;
`;
